import React, { FC, useContext, useRef, useState } from 'react'
import { CircularProgress, IconButton, InputAdornment, Tooltip } from '@mui/material'
// Icons
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined'
// Helpers & Context
import { AuthContext } from 'context/AuthContext'
import { APIHelper } from 'helpers/APIHelper'
// Types
import type { AxiosError } from 'axios'
import type { ChatMessage, ChatResponse } from 'types/chat'
import type { ResponseError } from 'types/report'
// Styles
import * as Styled from './FabricAgentChat.styled'

const FabricAgentChat: FC = () => {
    // Temporary hardcoding data agent ID (for the POC).
    const queryParams = new URLSearchParams(window.location.search)
    const dataAgentId = queryParams.get('data_agent') ?? 'e6d2d826-f12a-4611-86b3-2c08c19a9f34'

    const [isOpened, setIsOpened] = useState<boolean>(false)
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [inputMsg, setInputMsg] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)

    const listRef = useRef<HTMLDivElement>(null)
    const { sessionToken, accountBid } = useContext(AuthContext)
    const apiHelper = APIHelper.getInstance()

    const toggleChat = () => {
        setIsOpened((prev) => !prev)
        if (!isOpened) {
            setTimeout(() => scrollToLastMsg(), 100)
        }
    }

    const scrollToLastMsg = () => {
        setTimeout(() => {
            if (listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight
            }
        }, 50)
    }

    const sendMessageToAgent = async (event: React.FormEvent) => {
        event.preventDefault()
        const trimmedMsg = inputMsg.trim()
        if (!trimmedMsg || loading) return

        const userMessage: ChatMessage = { role: 'user', content: trimmedMsg }
        const updatedMessages = [...messages, userMessage]
        setMessages(updatedMessages)
        setInputMsg('')
        setLoading(true)
        scrollToLastMsg()

        try {
            const chatResponse = await apiHelper.get(
                `/chat?session_token=${sessionToken}&bid=${accountBid}&agent_message=${trimmedMsg}&data_agent=${dataAgentId}`
            )
            const { reply } = chatResponse.data as ChatResponse
            const agentMessage: ChatMessage = {
                role: 'agent',
                content: reply || 'Something went wrong!',
                error: !reply,
            }
            setMessages((prev) => [...prev, agentMessage])
        } catch (err) {
            const errorRes = err as AxiosError<ResponseError>
            const errorMsg = errorRes.response?.data?.error?.message ?? errorRes.message
            setMessages((prev) => [
                ...prev,
                {
                    role: 'agent',
                    content: `⛔️ Error ${errorRes.response?.data?.error?.code}: ${errorMsg}`,
                    error: true,
                },
            ])
        } finally {
            setLoading(false)
            scrollToLastMsg()
        }
    }

    return (
        <>
            {/* Floating action button */}
            <Tooltip title={isOpened ? 'Close chat' : 'Ask your data agent'} placement="left">
                <Styled.ChatToggle color="primary" id="agent-toggle-btn" onClick={toggleChat}>
                    {isOpened ? <CloseIcon /> : <AutoAwesomeIcon />}
                </Styled.ChatToggle>
            </Tooltip>

            {/* Chat popup */}
            {isOpened && (
                <Styled.ChatPopup elevation={8}>
                    {/* Header */}
                    <Styled.ChatHeader>
                        <AutoAwesomeIcon fontSize="small" />
                        <Styled.HeaderTitle>Amadeus Max</Styled.HeaderTitle>
                        <Styled.HeaderCloseButton
                            size="small"
                            onClick={toggleChat}
                            title="Close chat"
                            aria-label="Close chat"
                        >
                            <CloseIcon fontSize="small" />
                        </Styled.HeaderCloseButton>
                    </Styled.ChatHeader>

                    {/* Message list */}
                    <Styled.MessageList ref={listRef}>
                        {!messages.length && <Styled.AgentMsgBubble>How can I help you today?</Styled.AgentMsgBubble>}
                        {messages.map((msg, index) => {
                            return msg.role === 'agent' ? (
                                <Styled.AgentMsgBubble key={index} $isError={msg.error}>
                                    {msg.content}
                                </Styled.AgentMsgBubble>
                            ) : (
                                <Styled.UserMsgBubble key={index}>{msg.content}</Styled.UserMsgBubble>
                            )
                        })}
                        {loading && (
                            <Styled.ThinkingBox>
                                <CircularProgress size={14} />
                                <Styled.ThinkingText>Thinking…</Styled.ThinkingText>
                            </Styled.ThinkingBox>
                        )}
                    </Styled.MessageList>

                    {/* Input row */}
                    <Styled.InputRow onSubmit={sendMessageToAgent}>
                        <Styled.InputField
                            size="small"
                            fullWidth
                            placeholder="Ask anything..."
                            value={inputMsg}
                            onChange={(event) => setInputMsg(event.target.value)}
                            autoComplete="off"
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            type="submit"
                                            aria-label="Send message"
                                            size="small"
                                            color="primary"
                                        >
                                            <SendIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Styled.AiDisclaimer>AI responses can be inaccurate.</Styled.AiDisclaimer>
                    </Styled.InputRow>
                </Styled.ChatPopup>
            )}
        </>
    )
}

export default FabricAgentChat
